import { useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Collapse,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, MenuItem, Paper,
  Select, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestoreIcon from '@mui/icons-material/Restore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { AjustePista, ApiFallo, FilaPlan, PistaVista, PlanManual } from '../lib/api.js';
import { formatearTamano } from '../lib/eventos.js';
import { etiquetaIdioma, IDIOMAS } from '../lib/idiomas.js';

/** El editor de pistas: lo que en `video2plex` se hacía en una ventana de escritorio, sobre
 *  el fichero que ya está descargado.
 *
 *  Aquí la conversión ocurre sola en cuanto termina la descarga —no hay nadie mirando— así
 *  que esto no sustituye a la propuesta automática: la enseña, deja corregirla y vuelve a
 *  convertir. El original de aMule sigue en su carpeta, así que reconvertir no descarga
 *  nada; solo cuesta el tiempo de ffmpeg. */

/** Mueve un elemento del array sin mutarlo. El orden del array ES el orden de las pistas
 *  del MP4, así que esto no es cosmético: decide con qué audio arranca Plex. */
function mover<T>(xs: T[], desde: number, delta: number): T[] {
  const hasta = desde + delta;
  if (hasta < 0 || hasta >= xs.length) return xs;
  const copia = [...xs];
  const [x] = copia.splice(desde, 1);
  copia.splice(hasta, 0, x!);
  return copia;
}

function reemplazar(xs: AjustePista[], i: number, cambios: Partial<AjustePista>): AjustePista[] {
  return xs.map((x, j) => (j === i ? { ...x, ...cambios } : x));
}

interface PropsFila {
  ajuste: AjustePista;
  pista: PistaVista | undefined;
  posicion: number | null;
  esSubtitulo: boolean;
  copiados: Omit<AjustePista, 'indice' | 'descartar'> | null;
  onCambio: (cambios: Partial<AjustePista>) => void;
  onMover: (delta: number) => void;
  onCopiar: () => void;
  onPegar: () => void;
  primera: boolean;
  ultima: boolean;
}

function FilaPista({
  ajuste, pista, posicion, esSubtitulo, copiados, onCambio, onMover, onCopiar, onPegar, primera, ultima,
}: PropsFila) {
  if (!pista) return null;
  const fuera = ajuste.descartar;
  // En rojo solo lo que va a entrar en el MP4: el idioma de una pista descartada no acaba
  // en ninguna parte, y pintarla sería pedir que se arregle algo que no se usa.
  const sinIdioma = !fuera && !ajuste.idioma;

  return (
    <TableRow sx={{ opacity: fuera ? 0.45 : 1 }}>
      <TableCell sx={{ width: 44, textAlign: 'center', color: 'text.secondary' }}>
        {fuera ? '—' : posicion}
      </TableCell>

      <TableCell sx={{ width: 150 }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{pista.codec}</Typography>
        <Typography variant="caption" color="text.secondary">
          {[
            pista.canales ? `${pista.canales} canales` : null,
            pista.tamanoBytes ? formatearTamano(pista.tamanoBytes) : null,
            pista.esBitmap ? 'de imagen' : null,
          ].filter(Boolean).join(' · ') || `pista ${pista.indice}`}
        </Typography>
      </TableCell>

      <TableCell sx={{ width: 170 }}>
        <Autocomplete
          freeSolo
          size="small"
          options={IDIOMAS}
          value={ajuste.idioma ?? ''}
          disabled={fuera}
          getOptionLabel={etiquetaIdioma}
          // Dos manejadores, y el `motivo` no es opcional. MUI vuelve a escribir el campo con
          // `getOptionLabel(valor)` cada vez que el valor cambia, y ese repintado llega aquí
          // como un `onInputChange` con motivo `reset`: atenderlo guardaba la ETIQUETA como
          // idioma —poner `spa` dejaba la pista en `español (spa)`—, y lo que salía por el
          // otro lado era un código que no existe, escrito en el MP4 sin que nada fallara.
          // Escribir a mano es `input`; elegir del desplegable, `onChange`.
          onChange={(_e, v) => onCambio({ idioma: typeof v === 'string' && v.trim() ? v.trim() : null })}
          onInputChange={(_e, v, motivo) => {
            if (motivo !== 'reset') onCambio({ idioma: v.trim() ? v.trim() : null });
          }}
          renderInput={(params) => <TextField {...params} error={sinIdioma} placeholder="sin idioma" />}
        />
      </TableCell>

      <TableCell>
        <TextField
          size="small"
          fullWidth
          placeholder="sin título"
          disabled={fuera}
          value={ajuste.titulo ?? ''}
          onChange={(e) => onCambio({ titulo: e.target.value ? e.target.value : null })}
        />
      </TableCell>

      <TableCell sx={{ width: 70, textAlign: 'center' }}>
        <Checkbox
          size="small"
          disabled={fuera}
          checked={ajuste.porDefecto}
          onChange={(e) => onCambio({ porDefecto: e.target.checked })}
        />
      </TableCell>

      {esSubtitulo && (
        <TableCell sx={{ width: 70, textAlign: 'center' }}>
          <Checkbox
            size="small"
            disabled={fuera}
            checked={ajuste.forzado}
            onChange={(e) => onCambio({ forzado: e.target.checked })}
          />
        </TableCell>
      )}

      <TableCell sx={{ width: 190, whiteSpace: 'nowrap' }}>
        <IconButton size="small" disabled={fuera || primera} onClick={() => onMover(-1)}>
          <ArrowUpwardIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" disabled={fuera || ultima} onClick={() => onMover(1)}>
          <ArrowDownwardIcon fontSize="small" />
        </IconButton>
        <Tooltip title="Copiar idioma, título y marcas de esta pista">
          <span>
            <IconButton size="small" disabled={fuera} onClick={onCopiar}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={copiados ? 'Pegar aquí lo copiado' : 'Copia antes los metadatos de otra pista'}>
          <span>
            <IconButton size="small" disabled={fuera || !copiados} onClick={onPegar}>
              <ContentPasteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={fuera ? 'Volver a incluirla' : 'Quitarla del MP4'}>
          <IconButton size="small" onClick={() => onCambio({ descartar: !fuera })}>
            {fuera ? <RestoreIcon fontSize="small" /> : <DeleteOutlineIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

function TablaPistas({
  titulo, ajustes, pistas, esSubtitulo, copiados, onPlan, onCopiar, vacio,
}: {
  titulo: string;
  ajustes: AjustePista[];
  pistas: PistaVista[];
  esSubtitulo: boolean;
  copiados: Omit<AjustePista, 'indice' | 'descartar'> | null;
  onPlan: (xs: AjustePista[]) => void;
  onCopiar: (a: AjustePista) => void;
  vacio: string;
}) {
  const porIndice = useMemo(() => new Map(pistas.map((p) => [p.indice, p])), [pistas]);
  const conservadas = ajustes.filter((a) => !a.descartar);

  const pegarEnTodas = () => {
    if (!copiados) return;
    onPlan(ajustes.map((a) => (a.descartar ? a : { ...a, ...copiados })));
  };

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">
          {titulo} · {conservadas.length} de {ajustes.length}
        </Typography>
        <Button size="small" startIcon={<ContentPasteIcon />} disabled={!copiados} onClick={pegarEnTodas}>
          Pegar en todas
        </Button>
      </Stack>

      {!ajustes.length ? (
        <Alert severity="info" sx={{ py: 0.5 }}>{vacio}</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ textAlign: 'center' }}>#</TableCell>
              <TableCell>Códec</TableCell>
              <TableCell>Idioma</TableCell>
              <TableCell>Título</TableCell>
              <TableCell sx={{ textAlign: 'center' }}>Def.</TableCell>
              {esSubtitulo && <TableCell sx={{ textAlign: 'center' }}>Forz.</TableCell>}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {ajustes.map((a, i) => (
              <FilaPista
                key={a.indice}
                ajuste={a}
                pista={porIndice.get(a.indice)}
                posicion={a.descartar ? null : conservadas.indexOf(a) + 1}
                esSubtitulo={esSubtitulo}
                copiados={copiados}
                primera={i === 0}
                ultima={i === ajustes.length - 1}
                onCambio={(c) => onPlan(reemplazar(ajustes, i, c))}
                onMover={(d) => onPlan(mover(ajustes, i, d))}
                onCopiar={() => onCopiar(a)}
                onPegar={() => copiados && onPlan(reemplazar(ajustes, i, copiados))}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

/** Cómo se llama cada rango dinámico. El Dolby Vision de perfil 5 va en ámbar porque es el
 *  que no se puede pasar a SDR aquí: la propuesta lo copia y lo dice en su motivo. */
const NOMBRE_HDR: Record<'hdr10' | 'hlg' | 'dolby_vision', string> = {
  hdr10: 'HDR10',
  hlg: 'HLG',
  dolby_vision: 'Dolby Vision 5',
};

/** La propuesta automática con sus motivos, tal cual la calcula el servidor. Sin el motivo,
 *  una lista de «Quitar» parece arbitraria y nadie se fía de ella. */
function Propuesta({ motivos, pistas }: { motivos: FilaPlan[]; pistas: Map<string, PistaVista> }) {
  const [abierta, setAbierta] = useState(false);

  const texto = (a: FilaPlan['accion']) =>
    a === 'descartar' ? 'Quitar' : a === 'ocr' ? 'OCR' : a === 'recodificar' ? 'Recodificar' : 'Copiar';

  return (
    <Box>
      <Button
        size="small"
        onClick={() => setAbierta(!abierta)}
        endIcon={
          <ExpandMoreIcon sx={{ transform: abierta ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
        }
      >
        Por qué la propuesta decide esto
      </Button>
      <Collapse in={abierta}>
        <Table size="small" sx={{ mt: 1 }}>
          <TableBody>
            {motivos.map((m) => {
              const p = pistas.get(`${m.tipo}:${m.indice}`);
              return (
                <TableRow key={`${m.tipo}:${m.indice}`}>
                  <TableCell sx={{ width: 100 }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={m.accion === 'descartar' ? 'default' : 'primary'}
                      label={texto(m.accion)}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 60, textAlign: 'center', color: 'text.secondary' }}>
                    {m.orden ?? '—'}
                  </TableCell>
                  <TableCell sx={{ width: 190 }}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {m.tipo === 'video' ? 'vídeo' : m.tipo === 'audio' ? 'audio' : 'sub'} ·{' '}
                      {p?.codec ?? '?'} · {p?.idioma ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{m.motivo}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Collapse>
    </Box>
  );
}

export function EditorPistas({
  downloadId, titulo, abierto, onCerrar, onReconvertida, yaColocada = false,
}: {
  downloadId: string;
  titulo: string;
  abierto: boolean;
  onCerrar: () => void;
  onReconvertida: () => void;
  /** La película ya está en una biblioteca. Cambia lo que hay que advertir: el MP4 nuevo no
   *  la sustituye al convertirse, sino al moverlo desde Conversiones. */
  yaColocada?: boolean;
}) {
  const qc = useQueryClient();
  const { data, error, isLoading } = useQuery({
    queryKey: ['pistas', downloadId],
    queryFn: () => api.pistas(downloadId),
    enabled: abierto,
    // Analizar con ffprobe cuesta lo suyo en un fichero de seis gigas: no se repite sola.
    staleTime: Infinity,
    retry: false,
  });

  const [plan, setPlan] = useState<PlanManual | null>(null);
  const [copiados, setCopiados] = useState<Omit<AjustePista, 'indice' | 'descartar'> | null>(null);

  // Se arranca del plan guardado si alguien ya editó estas pistas; si no, de la propuesta.
  useEffect(() => {
    if (data) setPlan(data.guardado ?? data.propuesta);
  }, [data]);

  const reconvertir = useMutation({
    mutationFn: () => api.reconvertir(downloadId, plan!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pistas', downloadId] });
      onReconvertida();
      onCerrar();
    },
  });

  const olvidar = useMutation({
    mutationFn: () => api.olvidarPlan(downloadId),
    onSuccess: () => {
      if (data) setPlan(data.propuesta);
      void qc.invalidateQueries({ queryKey: ['pistas', downloadId] });
    },
  });

  const indicePistas = useMemo(() => {
    const m = new Map<string, PistaVista>();
    if (!data) return m;
    if (data.analisis.video) m.set(`video:${data.analisis.video.indice}`, data.analisis.video);
    for (const p of data.analisis.audio) m.set(`audio:${p.indice}`, p);
    for (const p of data.analisis.subtitulos) m.set(`subtitle:${p.indice}`, p);
    return m;
  }, [data]);

  const sinAudio = !!plan && !!data?.analisis.audio.length && plan.audio.every((a) => a.descartar);
  // Las que se conservan sin idioma, nombradas como se ven en la tabla: el aviso solo sirve
  // si dice cuál de las filas hay que mirar.
  const sinIdioma = useMemo(() => {
    if (!plan || !data) return [];
    // Por el número de la columna `#`, no por el índice de la pista en el fichero: el
    // primero es el que se ve en la tabla de arriba, y es ahí donde hay que buscarlas.
    const nombrar = (que: string, ajustes: AjustePista[], pistas: PistaVista[]) => {
      const conservadas = ajustes.filter((a) => !a.descartar);
      return conservadas
        .filter((a) => !a.idioma)
        .map(
          (a) =>
            `${que} nº ${conservadas.indexOf(a) + 1} ` +
            `(${pistas.find((x) => x.indice === a.indice)?.codec ?? '?'})`,
        );
    };
    return [
      ...nombrar('audio', plan.audio, data.analisis.audio),
      ...nombrar('subtítulo', plan.subtitulos, data.analisis.subtitulos),
    ];
  }, [plan, data]);

  // Lo que le falta al OCR para leer los subtítulos de imagen que el plan conserva, separado
  // en las dos faltas que tienen arreglos distintos: poner un lector, o poner el paquete de
  // un idioma. Mismo criterio que `porQueNoSeLee` en la API.
  //
  // El idioma que se mira es el DEL PLAN, no el del fichero: es el que se va a guardar, y
  // corregirlo aquí es justo lo que hace que la conversión deje de negarse. Sin idioma se
  // asume español, como allí; el mapeo de dos letras no hace falta, lo que llega ya viene
  // normalizado a ISO 639-2.
  const faltaOcr = useMemo(() => {
    const idioma = new Set<string>();
    let formato = 0;
    if (plan && data) {
      for (const a of plan.subtitulos) {
        if (a.descartar) continue;
        const p = data.analisis.subtitulos.find((x) => x.indice === a.indice);
        if (!p?.esBitmap) continue;
        if (!(p.codec === 'dvd_subtitle' ? data.ocr.vobsub : data.ocr.pgs)) {
          formato++;
          continue;
        }
        const lengua = a.idioma ?? 'spa';
        if (!data.ocr.idiomas.includes(lengua)) idioma.add(lengua);
      }
    }
    return { formato, idiomas: [...idioma] };
  }, [plan, data]);

  return (
    <Dialog open={abierto} onClose={onCerrar} fullWidth maxWidth="lg">
      <DialogTitle>
        Pistas de «{titulo}»
        {data?.guardado && (
          <Chip size="small" color="info" variant="outlined" label="plan editado" sx={{ ml: 1 }} />
        )}
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', py: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Analizando el fichero original con ffprobe…
            </Typography>
          </Stack>
        )}

        {error && (
          <Alert severity="error">
            <AlertTitle>No se pueden leer las pistas</AlertTitle>
            {(error as ApiFallo).message}
          </Alert>
        )}

        {data && plan && (
          <Stack spacing={2.5}>
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                {data.rutaOriginal}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip size="small" variant="outlined" label={`vídeo: ${data.analisis.video?.codec ?? '—'}`} />
                {data.analisis.alturaVideo && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      data.analisis.anchoVideo
                        ? `${data.analisis.anchoVideo}×${data.analisis.alturaVideo}`
                        : `${data.analisis.alturaVideo}p`
                    }
                  />
                )}
                {data.analisis.hdr && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={data.analisis.hdr === 'dolby_vision' ? 'warning' : 'default'}
                    label={NOMBRE_HDR[data.analisis.hdr]}
                  />
                )}
                {data.analisis.duracionSegundos && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${Math.round(data.analisis.duracionSegundos / 60)} min`}
                  />
                )}
                {data.analisis.tieneCapitulos && <Chip size="small" variant="outlined" label="con capítulos" />}
              </Stack>
            </Paper>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { md: 'center' } }}>
              <Button
                variant="outlined"
                startIcon={<AutoFixHighIcon />}
                onClick={() => setPlan(data.propuesta)}
              >
                Aplicar propuesta
              </Button>
              {data.guardado && (
                <Button size="small" color="inherit" disabled={olvidar.isPending} onClick={() => olvidar.mutate()}>
                  Olvidar el plan guardado
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Vídeo
                </Typography>
                <Select
                  size="small"
                  value={plan.recodificarVideo === null ? 'auto' : plan.recodificarVideo ? 'si' : 'no'}
                  onChange={(e) =>
                    setPlan({
                      ...plan,
                      recodificarVideo: e.target.value === 'auto' ? null : e.target.value === 'si',
                    })
                  }
                  sx={{ minWidth: 300 }}
                >
                  <MenuItem value="auto">Automático: copiar si ya es H.264 y no pasa de la altura máxima</MenuItem>
                  <MenuItem value="si">Recodificar siempre a H.264</MenuItem>
                  <MenuItem value="no">No recodificar nunca (solo cambiar contenedor, sin reescalar)</MenuItem>
                </Select>
              </Box>
            </Stack>

            <Propuesta motivos={data.motivos} pistas={indicePistas} />

            <Divider />

            <TablaPistas
              titulo="Audio"
              ajustes={plan.audio}
              pistas={data.analisis.audio}
              esSubtitulo={false}
              copiados={copiados}
              vacio="El fichero no trae ninguna pista de audio."
              onPlan={(xs) => setPlan({ ...plan, audio: xs })}
              onCopiar={(a) =>
                setCopiados({ idioma: a.idioma, titulo: a.titulo, porDefecto: a.porDefecto, forzado: a.forzado })
              }
            />

            <Divider />

            <TablaPistas
              titulo="Subtítulos"
              ajustes={plan.subtitulos}
              pistas={data.analisis.subtitulos}
              esSubtitulo
              copiados={copiados}
              vacio="El fichero no trae subtítulos."
              onPlan={(xs) => setPlan({ ...plan, subtitulos: xs })}
              onCopiar={(a) =>
                setCopiados({ idioma: a.idioma, titulo: a.titulo, porDefecto: a.porDefecto, forzado: a.forzado })
              }
            />

            {sinAudio && (
              <Alert severity="error">
                No queda ninguna pista de audio. Una película muda no es una opción: conserva
                al menos una.
              </Alert>
            )}

            {avisoSubtitulosDeImagen(faltaOcr.formato > 0)}

            {avisoIdiomaSinTesseract(faltaOcr.idiomas)}

            {avisoSinIdioma(sinIdioma)}

            {/* Lo que hay que advertir no es lo mismo en los dos casos, y la diferencia
                importa: con la película ya en Plex, esto la saca de «colocada» y la deja
                otra vez esperando en Conversiones hasta que alguien la mueva. */}
            {yaColocada ? (
              <Alert severity="warning">
                <AlertTitle>Esta película ya está en una biblioteca</AlertTitle>
                Reconvertir NO la toca todavía: el fichero que Plex tiene indexado se queda
                donde está mientras ffmpeg trabaja sobre el original de aMule. El MP4 nuevo
                espera en Conversiones y sustituye al de la biblioteca cuando lo muevas allí
                —a esa misma, o dejarás dos copias—. Mientras tanto la descarga deja de
                figurar como colocada.
              </Alert>
            ) : (
              <Alert severity="info">
                <AlertTitle>Reconvertir vuelve a pasar ffmpeg sobre el original</AlertTitle>
                No se descarga nada: el fichero de aMule sigue en su carpeta y es de donde se
                lee. El MP4 que ahora espera en conversiones se reemplaza.
              </Alert>
            )}

            {reconvertir.isError && (
              <Alert severity="error" onClose={() => reconvertir.reset()}>
                {(reconvertir.error as ApiFallo).message}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCerrar}>Cerrar</Button>
        <Button
          variant="contained"
          disabled={!plan || sinAudio || reconvertir.isPending}
          onClick={() => reconvertir.mutate()}
        >
          {/* «Encolando», no «convirtiendo»: la petición vuelve en cuanto el trabajo entra
              en la cola, y la conversión en sí puede tardar horas. Por dónde va se sigue en
              Conversiones y en el registro, no aquí con un botón apagado. */}
          {reconvertir.isPending ? 'Encolando…' : 'Guardar y reconvertir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** El otro motivo por el que un subtítulo de imagen no se lee, y el que no se ve venir:
 *  tesseract necesita el paquete de SU idioma, y sin él no lee esa pista aunque el lector
 *  del formato esté. Como el de arriba, esto anticipa un rechazo (D-18) y ahorra el viaje;
 *  a diferencia de él, tiene un arreglo que se hace aquí mismo: corregir el idioma. */
function avisoIdiomaSinTesseract(idiomas: string[]) {
  if (!idiomas.length) return null;
  return (
    <Alert severity="warning">
      Hay subtítulos de imagen marcados para conservar en un idioma que{' '}
      <code>tesseract</code> no tiene instalado ({idiomas.join(', ')}): cada idioma es un
      paquete aparte de la imagen, y sin el suyo esa pista no se lee. La conversión se negará
      a correr en vez de dar una película sin ellos. Si el idioma está mal puesto, corrígelo
      arriba; si no, quítalos del plan o añade el paquete. En Ajustes → Conversión a MP4 está
      la lista de los que hay.
    </Alert>
  );
}

/** Las pistas en rojo, explicadas. A diferencia del aviso de los subtítulos de imagen, este
 *  NO anticipa un fallo: un plan editado a mano se convierte con pistas sin idioma, porque
 *  quien lo edita las está mirando y a veces el idioma de verdad no se sabe. Lo que dice es
 *  qué se decide a ciegas si se deja así, que es lo que no se ve desde aquí. */
function avisoSinIdioma(pistas: string[]) {
  if (!pistas.length) return null;
  return (
    <Alert severity="warning">
      <AlertTitle>
        {pistas.length === 1 ? 'Una pista sin idioma' : `${pistas.length} pistas sin idioma`}
      </AlertTitle>
      El fichero no dice el idioma de {pistas.join(', ')}. Con ese hueco la conversión
      automática se para y pide que lo mires —es esto—, porque de ahí cuelgan dos cosas que
      si no se adivinan: el orden de los audios, que es con el que arranca Plex, y el modelo
      con el que <code>tesseract</code> lee un subtítulo de imagen, que sin idioma se asume
      español y puede dar un SRT ilegible que nadie revisa. Puedes convertir igual —lo
      decides tú, viendo las pistas—, pero si sabes cuál es, ponlo.
    </Alert>
  );
}

/** Se avisa ANTES de convertir, no después: con el lector de ese formato ausente, un
 *  subtítulo de imagen que se deja marcado no impide guardar el plan, pero la conversión se
 *  negará a correr (D-18). Decirlo aquí ahorra el viaje de ida y vuelta. */
function avisoSubtitulosDeImagen(hay: boolean) {
  if (!hay) return null;
  return (
    <Alert severity="warning">
      Hay subtítulos de imagen marcados para conservar y este servidor no tiene con qué leer
      ese formato: los PGS los lee <code>pgsrip</code> y los VobSub <code>subtile-ocr</code>,
      y falta el que toca. MP4 no admite subtítulos de imagen sin pasarlos por OCR, así que la
      conversión fallará en vez de dar una película sin ellos: quítalos del plan, o mira
      Ajustes → Conversión a MP4 para ver qué falta.
    </Alert>
  );
}
